import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import packageJson from '../../../../package.json';
import {
  EXPORT_THEME,
  formatExportTimestamp,
  loadExportLogoDataUrl,
} from '../theme';
import { buildStoreBlocks, getEventExportBasename } from '../eventPlanData';

const BRAND_ORANGE = [246, 139, 31];
const BRAND_GREEN = [57, 94, 20];
const STRIPE_FILL = [247, 242, 234];

function buildPdfTableHead(block) {
  return [
    [{
      content: block.name,
      colSpan: 2,
      styles: {
        halign: 'center',
        fillColor: BRAND_ORANGE,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
      },
    }],
    [{
      content: 'Fascia oraria',
      styles: { fillColor: BRAND_GREEN, textColor: 255, fontStyle: 'bold' },
    }, {
      content: 'Partecipante',
      styles: { fillColor: BRAND_GREEN, textColor: 255, fontStyle: 'bold' },
    }],
  ];
}

function buildPdfTableBody(block) {
  const body = [];

  block.meta.forEach((line) => {
    body.push([{
      content: line,
      colSpan: 2,
      styles: {
        fontStyle: 'italic',
        textColor: 90,
        fillColor: STRIPE_FILL,
        fontSize: 7,
      },
    }]);
  });

  if (block.rows.length === 0) {
    body.push(['—', '—']);
    return body;
  }

  block.rows.forEach((row, index) => {
    body.push([
      { content: row[0], styles: index % 2 === 1 ? { fillColor: STRIPE_FILL } : {} },
      { content: row[1], styles: index % 2 === 1 ? { fillColor: STRIPE_FILL } : {} },
    ]);
  });

  return body;
}

function drawPdfHeader(doc, { title, logoDataUrl }) {
  const margin = 14;
  let textX = margin;

  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', margin, 8, 16, 16);
    textX = margin + 20;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_GREEN);
  doc.text(EXPORT_THEME.organization, textX, 14);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(31, 41, 55);
  doc.text(title || 'Piano turni', textX, 21);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(95, 107, 122);
  doc.text(`Esportato il ${formatExportTimestamp()}`, textX, 27);

  return 34;
}

function drawPdfFooter(doc) {
  const pageCount = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `${EXPORT_THEME.organization} · Gestore inviti v${packageJson.version}`,
      14,
      pageHeight - 8
    );
    doc.text(`Pagina ${page} di ${pageCount}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
  }
}

export async function exportEventToPdf(event, participants) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const blocks = buildStoreBlocks(event, participants);
  const logoDataUrl = await loadExportLogoDataUrl();
  const startY = drawPdfHeader(doc, {
    title: event.name || 'Evento',
    logoDataUrl,
  });

  const margin = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  const gap = 6;

  if (blocks.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(95, 107, 122);
    doc.text('Nessun negozio configurato.', margin, startY);
  } else {
    const availableWidth = pageWidth - margin * 2 - gap * (blocks.length - 1);
    const tableWidth = availableWidth / blocks.length;

    blocks.forEach((block, index) => {
      autoTable(doc, {
        startY,
        margin: { left: margin + index * (tableWidth + gap), right: 0 },
        tableWidth,
        head: buildPdfTableHead(block),
        body: buildPdfTableBody(block),
        theme: 'grid',
        styles: {
          fontSize: 7,
          cellPadding: 2.2,
          lineColor: [229, 213, 192],
          lineWidth: 0.1,
          textColor: [31, 41, 55],
          overflow: 'linebreak',
        },
        headStyles: {
          fillColor: BRAND_GREEN,
          textColor: 255,
          fontStyle: 'bold',
        },
        columnStyles: {
          0: { cellWidth: tableWidth * 0.38 },
          1: { cellWidth: tableWidth * 0.62 },
        },
      });
    });
  }

  drawPdfFooter(doc);
  doc.save(`${getEventExportBasename(event)}.pdf`);
}
